import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Drivers, Scenarios } from '@gql/base-types';
import { useGetScheduleScenariosQuery } from '../queries/getScheduleScenarios.query.generated';
import { useGetDriversQuery } from '../../drivers/queries/getDrivers.query.generated';
import { useRooftop } from '@store';
import { getAlgorithm, prepareDriverScenariosForAlgorithm, type Scenario as SdkScenario } from '@hopdrive/sdk-appointments';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';

/**
 * Timeline Schedule Data Hook
 *
 * Uses scenario-first query approach for optimal performance:
 * - Queries scenarios directly with their moves already grouped
 * - ~60% reduction in data payload compared to move-first approach
 * - Eliminates circular redundancy in query structure
 * - Filters out offered scenarios from unassigned row
 */

// Create a mock driver for a hopdrive-managed customer (anonymized)
const createAnonymousDriver = (index: number): Drivers => {
  return {
    id: -index, // Negative ID to avoid conflicts
    status: 'online',
    user: {
      display_name: `HopDriver ${index}`,
      avatar_url: null,
    },
  } as unknown as Drivers;
};

export interface ScheduleTimelineRow {
  driver: Drivers | null; // null for "Unassigned" row
  scenarios: Scenarios[]; // Assigned scenarios (for UI display only)
  offeredScenarios?: Scenarios[]; // Offered scenarios (status: offered) (for UI display only)
  allScenarios: Scenarios[]; // All scenarios merged (for algorithm consumption) - includes assigned + distributed + offered
}

// `prepareDriverScenariosForAlgorithm` is typed with @hopdrive/sdk-appointments'
// own GraphQL-codegen projection types (Scenario), which describe the same runtime
// data as dealer-portal's base-types `Scenarios` but differ structurally (field
// nullability). We feed it `Scenarios` and consume the result as `Scenarios`,
// bridging the projection boundary here.
type PreparedScenarioRows = {
  allDriverScenarios: Scenarios[][];
  offeredScenariosByDriver: Map<number, Scenarios[]>;
  driverCount: number;
};

interface UseScheduleDataProps {
  selectedDate: string; // YYYY-MM-DD format
}

export const useScheduleData = ({ selectedDate }: UseScheduleDataProps) => {
  const rooftop = useRooftop();
  const customerId = rooftop?.id || 0;

  // Calculate date range for the selected day
  const { startDate, endDate } = useMemo(() => {
    const start = dayjs(selectedDate).startOf('day').format();
    const end = dayjs(selectedDate).endOf('day').format();
    return { startDate: start, endDate: end };
  }, [selectedDate]);

  // Fetch scenarios for the selected date (scenario-first approach)
  const {
    data: scenariosData,
    loading: scenariosLoading,
    error: scenariosError,
    refetch: refetchScenarios,
  } = useGetScheduleScenariosQuery({
    variables: {
      start: startDate,
      end: endDate,
      customerId,
    },
    pollInterval: 5000, // Auto-refresh every 5 seconds
    fetchPolicy: 'no-cache',
    skip: !customerId,
  });

  // Standard (hopdrive-managed) uses anonymous HopDrivers; self-managed uses real drivers.
  const isStandardTier = !useValetSelfManaged();

  // Fetch drivers (self-managed only — hopdrive-managed uses anonymous HopDrivers)
  const {
    data: driversData,
    loading: driversLoading,
    error: driversError,
    refetch: refetchDrivers,
  } = useGetDriversQuery({
    variables: { customerId },
    skip: !customerId || isStandardTier,
    fetchPolicy: 'cache-and-network',
  });

  // Get configuration from rooftop config and instantiate algorithm
  const { driverCount, algorithm, moveBufferMinutes } = useMemo(() => {
    if (!rooftop?.config) {
      return { driverCount: 0, algorithm: getAlgorithm('balance-drivers-v1'), moveBufferMinutes: 0 };
    }

    try {
      const config = typeof rooftop.config === 'string' ? JSON.parse(rooftop.config) : rooftop.config;
      const algorithmName = config?.appointment_management?.strategy || 'balance-drivers-v1';

      return {
        driverCount: config?.appointment_management?.driver_count || 2,
        algorithm: getAlgorithm(algorithmName),
        moveBufferMinutes: config?.appointment_management?.move_buffer_minutes || 0,
      };
    } catch (e) {
      console.error('Error parsing rooftop config:', e);
      return { driverCount: 0, algorithm: getAlgorithm('balance-drivers-v1'), moveBufferMinutes: 0 };
    }
  }, [rooftop?.config, rooftop?.id, rooftop?.name]);

  // Process and group data for timeline
  const timelineData = useMemo(() => {
    // Scenarios are already fetched and grouped with their moves!
    const allScenarios = (scenariosData?.scenarios || []) as Scenarios[];

    // Hopdrive-managed uses anonymous drivers; self-managed uses actual drivers.
    if (isStandardTier) {
      // Use shared utility for consistent scenario preparation
      const { allDriverScenarios, offeredScenariosByDriver, driverCount: actualDriverCount } =
        prepareDriverScenariosForAlgorithm({
          scenarios: allScenarios as unknown as SdkScenario[],
          tier: 'standard',
          algorithm,
          driverCount,
          bufferMinutes: moveBufferMinutes,
          timezone: rooftop?.location?.timezone || 'America/Los_Angeles',
        }) as unknown as PreparedScenarioRows;

      // Early return if no driver count
      if (actualDriverCount === 0) {
        return [];
      }

      // Convert to timeline row format
      const driverRows: ScheduleTimelineRow[] = [];
      for (let i = 0; i < actualDriverCount; i++) {
        // Split the combined scenarios back into assigned and offered for display
        const allScenariosForRow = allDriverScenarios[i] || [];

        // Separate offered scenarios for display (they have separate visual treatment)
        const offeredForThisRow: Scenarios[] = [];
        const assignedForThisRow: Scenarios[] = [];

        allScenariosForRow.forEach((scenario) => {
          // Check if this scenario is in the offered map
          let isOffered = false;
          offeredScenariosByDriver.forEach((offeredScenarios) => {
            if (offeredScenarios.some((s) => s.id === scenario.id)) {
              isOffered = true;
            }
          });

          if (isOffered) {
            offeredForThisRow.push(scenario);
          } else {
            assignedForThisRow.push(scenario);
          }
        });

        driverRows.push({
          driver: createAnonymousDriver(i + 1),
          scenarios: assignedForThisRow,       // UI display only
          offeredScenarios: offeredForThisRow, // UI display only
          allScenarios: allDriverScenarios[i] || [], // For algorithm (MERGED)
        });
      }

      return driverRows;
    } else {
      // Self-managed - use actual drivers
      if (!driversData?.drivers) {
        return [];
      }

      const drivers = driversData.drivers;

      // Filter for active/online drivers and sort by ID for consistent ordering
      const activeDrivers = drivers
        .filter((d) => d.status === 'online' || d.status === 'offline' || d.status === 'training')
        .sort((a, b) => a.id - b.id);

      // Use shared utility for consistent scenario preparation
      const { allDriverScenarios, offeredScenariosByDriver } = prepareDriverScenariosForAlgorithm({
        scenarios: allScenarios as unknown as SdkScenario[],
        tier: 'lite',
        algorithm,
        activeDrivers: activeDrivers.map((d) => ({ id: d.id })),
      }) as unknown as PreparedScenarioRows;

      // Convert to timeline row format
      const driverRows: ScheduleTimelineRow[] = activeDrivers.map((driver, index) => {
        const allScenariosForDriver = allDriverScenarios[index] || [];

        // Separate offered scenarios for display
        const offeredForDriver = offeredScenariosByDriver.get(driver.id) || [];
        const assignedForDriver = allScenariosForDriver.filter(
          (scenario) => !offeredForDriver.some((s) => s.id === scenario.id)
        );

        return {
          driver: driver as Drivers,
          scenarios: assignedForDriver,        // UI display only
          offeredScenarios: offeredForDriver,  // UI display only
          allScenarios: allScenariosForDriver, // For algorithm (MERGED)
        };
      });

      // Add unassigned row at the top if there are unassigned scenarios
      // (scenarios with no driver_id and no active offers)
      const unassignedScenarios = allScenarios.filter((scenario) => {
        const firstMoveLink = scenario.moves?.[0];
        const hasDriverId = !!firstMoveLink?.move?.driver_id;

        // Check if has active offers
        const hasActiveOffer = scenario.bundletoscenarios?.some((link) =>
          link.bundle?.offers?.some((offer) => offer.status === 'offered' || offer.status === 'accepted')
        );

        return !hasDriverId && !hasActiveOffer;
      });

      if (unassignedScenarios.length > 0) {
        driverRows.unshift({
          driver: null,
          scenarios: unassignedScenarios,
          allScenarios: unassignedScenarios, // Same as scenarios for unassigned
        });
      }

      return driverRows;
    }
  }, [scenariosData, driversData, isStandardTier, driverCount, algorithm, moveBufferMinutes, rooftop?.location?.timezone]);

  const loading = scenariosLoading || driversLoading;
  const error = scenariosError || driversError;

  const refetch = () => {
    refetchScenarios();
    refetchDrivers();
  };

  // Extract moves from scenarios for backward compatibility
  const moves = useMemo(() => {
    const scenarios = scenariosData?.scenarios || [];
    const allMoves = scenarios.flatMap((scenario) =>
      (scenario.moves || []).map((link) => link.move).filter(Boolean)
    );
    return allMoves;
  }, [scenariosData]);

  return {
    timelineData,
    loading,
    error,
    refetch,
    moves,
    drivers: driversData?.drivers || [],
    scenarios: scenariosData?.scenarios || [],
    algorithm,
    driverCount,
    moveBufferMinutes,
  };
};
